import { useState, useEffect } from 'react';
import { Box, Typography, Chip, CircularProgress, Paper } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import {} from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';

const DATA_PIPELINE_URL = import.meta.env.VITE_DATAPIPE_URL || 'http://localhost:3002';

interface SyncLog {
  id: string;
  connection_id: string;
  object_type: string;
  records_added: number;
  records_updated: number;
  records_deleted: number;
  synced_at: string;
  status: string;
  sync_mode: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle2 size={ICON_SIZES.md} strokeWidth={ICON_STROKE} color="#10b981" />;
    case 'failed':
      return <XCircle size={ICON_SIZES.md} strokeWidth={ICON_STROKE} color="#ef4444" />;
    case 'running':
      return <Clock size={ICON_SIZES.md} strokeWidth={ICON_STROKE} color="#f59e0b" />;
    default:
      return null;
  }
};

const formatDuration = (ms: number | null) => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

export function SyncLogTable() {
  const { data: user } = useCurrentUser();
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      if (!user?.tenant_id) return;

      try {
        const response = await fetch(`${DATA_PIPELINE_URL}/api/sync-logs?tenant_id=${user.tenant_id}&limit=100`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setLogs(data.logs || []);
        }
      } catch (error) {
        console.error('Failed to fetch sync logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [user]);

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{getStatusIcon(params.value)}</Box>,
    },
    {
      field: 'object_type',
      headerName: 'Object Type',
      width: 150,
    },
    {
      field: 'sync_mode',
      headerName: 'Mode',
      width: 120,
      renderCell: (params) => <Chip label={params.value} size="small" variant="outlined" />,
    },
    {
      field: 'records_added',
      headerName: 'Added',
      width: 100,
      type: 'number',
    },
    {
      field: 'records_updated',
      headerName: 'Updated',
      width: 100,
      type: 'number',
    },
    {
      field: 'records_deleted',
      headerName: 'Deleted',
      width: 100,
      type: 'number',
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 100,
      renderCell: (params) => formatDuration(params.value),
    },
    {
      field: 'synced_at',
      headerName: 'Synced At',
      width: 180,
      renderCell: (params) => new Date(params.value).toLocaleString(),
    },
    {
      field: 'error_message',
      headerName: 'Error',
      width: 200,
      renderCell: (params) =>
        params.value ? (
          <Typography variant="caption" color="error" noWrap>
            {params.value}
          </Typography>
        ) : (
          '-'
        ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No sync history yet
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={logs}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
      />
    </Box>
  );
}
